import { useCallback } from 'react';
import { openLink } from '@tma.js/sdk-react';

import { env } from '@/env';
import { useTrackViewMutation } from '@/services/newsApi';

/**
 * Article share hook for the Telegram Mini App.
 *
 * Mobile-app uses native `Share.share()` for the system share sheet. In the
 * Telegram WebView the closest equivalent is `t.me/share/url`, which Telegram
 * routes through the user's Telegram contacts share sheet — exactly the
 * audience that matters for a Telegram bot.
 *
 * We open it via `openLink.ifAvailable` so the WebView delegates to the
 * Telegram client's native share UI (or the system browser as a fallback).
 *
 * Note: deliberately no Firebase Dynamic Links — the shared link is the public
 * web canonical URL `{env.publicSiteUrl}/news/{slug}`.
 *
 * The ORIGIN comes from `VITE_PUBLIC_SITE_URL` (default `https://next.qlash.gg`,
 * Luca's decision 2026-09-06) and no longer from a constant here. It used to read
 * `https://tma.qlash.gg/news/{slug}` — this app's own origin, which serves the SPA
 * shell for any path: measured 2026-09-06 23:07Z, `tma.qlash.gg/news/<any slug>`
 * answers 200 with `<title>QLASH</title>` and ZERO `og:` tags, for a Telegram
 * crawler UA too. So every share made so far produced a generic preview pointing
 * at a page the recipient cannot read — a failure that LOOKED like a success.
 *
 * The `/news/{slug}` path below is MEASURED, not assumed. This paragraph used to
 * say the opposite — truthfully, when written — and is updated rather than left to
 * mislead in the tone of caution, which is the exact failure the paragraph above
 * describes: a line that was true when written and that nobody re-reads.
 *
 * [M or53, 2026-09-06 23:2xZ — measured on the `qlash-web` repo, not from here]
 *   Route `app/(public)/news/[slug]/page.tsx` (Next App Router); `loadArticle`
 *   queries qnews at `/qnews/${encodeURIComponent(slug)}` — the SLUG reaches the
 *   API literally, not an id. Negative controls: `/news/<made-up-slug>`,
 *   `/news/<ObjectId>` and `/a-nonexistent-route` all answer 404. Positive:
 *   a real `/news/<slug>` answers 200 carrying og:title, og:description,
 *   og:image 1200x630 and og:type=article, for a Telegram crawler UA too.
 *
 * 🔑 The 200-shell hazard described above does NOT apply to that origin, and this
 * was proven rather than argued away: `qlash-web` is not an SPA but Next
 * server-rendered (OpenNext on Cloudflare Workers), with no index.html fallback —
 * a wrong path there 404s visibly. The hazard remains fully real for tma.qlash.gg.
 */
export function useShareArticle(articleId: string, slug: string, title: string) {
  const [trackView] = useTrackViewMutation();

  return useCallback(() => {
    const url = `${env.publicSiteUrl}/news/${slug}`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    const opened = openLink.ifAvailable(shareUrl);
    // Telemetry: tag the article as shared whether or not the share UI
    // actually completed (Telegram doesn't surface that). Best-effort.
    trackView({ articleId, shared: true }).catch(() => {});
    if (!opened) {
      // Outside Telegram: copy URL to clipboard as a graceful fallback.
      try {
        void navigator.clipboard.writeText(url);
      } catch {
        /* clipboard unavailable */
      }
    }
  }, [articleId, slug, title, trackView]);
}
